import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Shield, Save, Package, FolderTree, FileInput, CreditCard, RefreshCcw, BarChart3, Users, Lock, Info, Code2 } from 'lucide-react';
import { API } from '@/lib/apiBase';

// Module configuration with icons and labels (order preserved)
const MODULE_ORDER = [
  'products',
  'categories',
  'entries',
  'costs',
  'refacturation',
  'statistics',
  'users',
  'development',
];

const MODULE_CONFIG = {
  products: { label: 'Produits', icon: Package },
  categories: { label: 'Catégories', icon: FolderTree },
  entries: { label: 'Entrées', icon: FileInput },
  costs: { label: 'Coûts', icon: CreditCard },
  refacturation: { label: 'Refacturation', icon: RefreshCcw },
  statistics: { label: 'Statistiques', icon: BarChart3 },
  users: { label: 'Utilisateurs', icon: Users },
  development: { label: 'Développement', icon: Code2 },
};

// Permission level configuration
const LEVEL_CONFIG = {
  none: { label: 'Aucun accès', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: '🔴' },
  read: { label: 'Lecture seule', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: '🔵' },
  write: { label: 'Modification', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: '🟢' },
};

// Role configuration
const ROLE_CONFIG = {
  admin: { label: 'Administrateur', color: 'bg-primary text-black' },
  gestionnaire: { label: 'Gestionnaire', color: 'bg-teal-500 text-black' },
  utilisateur: { label: 'Utilisateur', color: 'bg-secondary text-foreground' },
  developpeur: { label: 'Développeur', color: 'bg-violet-500 text-white' },
};

export const PermissionsMatrix = () => {
  const { token } = useAuth();
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Local state for editing
  const [editedPermissions, setEditedPermissions] = useState({
    gestionnaire: {},
    utilisateur: {},
    developpeur: {},
  });

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${API}/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPermissions(response.data);
      setEditedPermissions({
        gestionnaire: response.data.gestionnaire || {},
        utilisateur: response.data.utilisateur || {},
        developpeur: response.data.developpeur || {},
      });
    } catch (error) {
      toast.error('Erreur lors du chargement des permissions');
      console.error('Error fetching permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, [token]);

  const handlePermissionChange = (role, module, level) => {
    if (role === 'admin') return; // Admin permissions are locked

    setEditedPermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [module]: level
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/permissions`,
        editedPermissions,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPermissions(response.data);
      setHasChanges(false);
      toast.success('Permissions enregistrées avec succès');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement des permissions');
      console.error('Error saving permissions:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (permissions) {
      setEditedPermissions({
        gestionnaire: permissions.gestionnaire || {},
        utilisateur: permissions.utilisateur || {},
        developpeur: permissions.developpeur || {},
      });
      setHasChanges(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const modules = MODULE_ORDER;
  const roles = ['admin', 'gestionnaire', 'utilisateur', 'developpeur'];

  return (
    <Card className="bg-card border-border" data-testid="permissions-matrix">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-foreground font-heading flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Gestion des Permissions
            </CardTitle>
            <CardDescription className="mt-1">
              Définissez les droits d'accès pour chaque rôle sur les différents modules
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <Button
                variant="outline"
                onClick={handleReset}
                data-testid="reset-permissions-btn"
              >
                Annuler
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="bg-primary hover:bg-primary/90 text-black"
              data-testid="save-permissions-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Info banner */}
        <div className="mb-6 p-4 rounded-lg bg-secondary/50 border border-border flex items-start gap-3">
          <Info className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
          <div className="text-sm text-muted-foreground">
            <p className="mb-2">
              <strong className="text-foreground">Niveaux de permission :</strong>
            </p>
            <ul className="space-y-1">
              <li className="flex items-center gap-2">
                <span>🔴</span> <strong>Aucun accès</strong> : Module masqué dans le menu
              </li>
              <li className="flex items-center gap-2">
                <span>🔵</span> <strong>Lecture seule</strong> : Consultation uniquement (boutons Ajouter/Modifier/Supprimer désactivés)
              </li>
              <li className="flex items-center gap-2">
                <span>🟢</span> <strong>Modification</strong> : Accès complet (CRUD)
              </li>
            </ul>
          </div>
        </div>

        {/* Permissions Matrix Table */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground w-48">Module</TableHead>
                {roles.map(role => (
                  <TableHead key={role} className="text-center min-w-40">
                    <div className="flex flex-col items-center gap-1">
                      <Badge className={ROLE_CONFIG[role].color}>
                        {ROLE_CONFIG[role].label}
                      </Badge>
                      {role === 'admin' && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Lock className="w-3 h-3" /> Verrouillé
                        </span>
                      )}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {modules.map(module => {
                const ModuleIcon = MODULE_CONFIG[module].icon;
                return (
                  <TableRow 
                    key={module} 
                    className="border-border table-row-hover"
                    data-testid={`permission-row-${module}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <ModuleIcon className="w-5 h-5 text-primary" />
                        <span className="font-medium text-foreground">
                          {MODULE_CONFIG[module].label}
                        </span>
                      </div>
                    </TableCell>
                    {roles.map(role => {
                      const isAdmin = role === 'admin';
                      const currentLevel = isAdmin 
                        ? 'write' 
                        : (editedPermissions[role]?.[module] || 'read');
                      
                      return (
                        <TableCell key={role} className="text-center">
                          {isAdmin ? (
                            // Admin: locked to "Modification"
                            <div className="flex justify-center">
                              <Badge 
                                className={`${LEVEL_CONFIG.write.color} border cursor-not-allowed opacity-75`}
                              >
                                {LEVEL_CONFIG.write.icon} {LEVEL_CONFIG.write.label}
                              </Badge>
                            </div>
                          ) : (
                            // Gestionnaire & Utilisateur: editable
                            <Select
                              value={currentLevel}
                              onValueChange={(value) => handlePermissionChange(role, module, value)}
                            >
                              <SelectTrigger 
                                className="w-40 mx-auto bg-secondary/50 border-border"
                                data-testid={`permission-select-${role}-${module}`}
                              >
                                <SelectValue>
                                  <Badge className={`${LEVEL_CONFIG[currentLevel].color} border`}>
                                    {LEVEL_CONFIG[currentLevel].icon} {LEVEL_CONFIG[currentLevel].label}
                                  </Badge>
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent className="bg-card border-border">
                                <SelectItem value="none">
                                  <div className="flex items-center gap-2">
                                    <span>🔴</span>
                                    <span>Aucun accès</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="read">
                                  <div className="flex items-center gap-2">
                                    <span>🔵</span>
                                    <span>Lecture seule</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="write">
                                  <div className="flex items-center gap-2">
                                    <span>🟢</span>
                                    <span>Modification</span>
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* Footer note */}
        <div className="mt-6 text-sm text-muted-foreground text-center">
          Les modifications seront appliquées immédiatement à tous les utilisateurs du rôle concerné.
        </div>
      </CardContent>
    </Card>
  );
};
